import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Calendar, Users as UsersIcon, Camera, Mountain } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { attractions } from '../data/mock';

const About = () => {
  return (
    <>
      <Helmet>
        <title>{`About Us | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="Learn about Hotel Suvidha Deluxe Haridwar, our location, services, and commitment to guest comfort." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1585083783155-76ae62b2914e?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Discover Haridwar</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Gateway to the Gods - Experience the spiritual heart of India
          </p>
        </div>
      </section>

      {/* About Haridwar */}
      <section className="container mx-auto px-4 -mt-12">
        <Card className="border-0 shadow-2xl mb-20">
          <CardContent className="p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-navy-900 mb-6 font-serif">The Holy City</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Haridwar, meaning "Gateway to God," is one of the seven holiest places in Hinduism. Located in Uttarakhand, where the sacred Ganges River emerges from the Himalayas, this ancient city attracts millions of pilgrims and tourists every year.
                  </p>
                  <p>
                    The city is renowned for its evening Ganga Aarti at Har Ki Pauri, where priests perform elaborate rituals with fire, flowers, and prayers. The sight of thousands of diyas (oil lamps) floating on the river at dusk creates an unforgettable spiritual experience.
                  </p>
                  <p>
                    Beyond its spiritual significance, Haridwar serves as a base for the Char Dham pilgrimage and offers easy access to Rishikesh, the yoga capital of the world, and the scenic hill stations of Uttarakhand.
                  </p>
                </div>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/haridwar.webp"
                  alt="Haridwar"
                  loading="lazy"
                  width={600}
                  height={400}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Key Attractions */}
      <section className="container mx-auto px-4 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Must-Visit Places</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore the sacred sites and natural beauty around Haridwar
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {attractions.map((attraction) => (
            <Link
              key={attraction.id}
              to={`/attractions/${attraction.slug || attraction.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="block"
              aria-label={`Read about: ${attraction.name}`}
            >
              <Card className="border-0 shadow-xl overflow-hidden group hover:shadow-2xl transition-shadow cursor-pointer">
                <div className="relative h-64 overflow-hidden">
                <img
                  src={attraction.image}
                  alt={attraction.name}
                  loading="lazy"
                  width={600}
                  height={256}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                  <div className="absolute top-4 left-4 bg-gold-600 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                    <MapPin size={16} className="inline mr-1" />
                    {attraction.distance}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors">{attraction.name}</h3>
                  <p className="text-gray-600">{attraction.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Travel Information */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Travel Information</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Plan your journey to Haridwar with ease
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mountain className="text-gold-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">By Air</h3>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Jolly Grant Airport, Dehradun</strong>
                </p>
                <p className="text-gray-500 text-sm">35 km away (1 hour drive)</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UsersIcon className="text-gold-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">By Train</h3>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Haridwar Junction</strong>
                </p>
                <p className="text-gray-500 text-sm">Well connected to major cities</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-gold-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">By Road</h3>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>NH-58 Highway</strong>
                </p>
                <p className="text-gray-500 text-sm">214 km from Delhi (5 hours)</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Best Time to Visit */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Best Time to Visit</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the perfect season for your spiritual journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-gold-600" size={24} />
                  <h3 className="text-xl font-bold text-navy-900">Winter (Oct-Feb)</h3>
                </div>
                <p className="text-gray-600 mb-2">
                  <strong>Best Season</strong> - Pleasant weather ideal for sightseeing
                </p>
                <p className="text-sm text-gray-500">Temperature: 5°C - 25°C</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-gold-600" size={24} />
                  <h3 className="text-xl font-bold text-navy-900">Summer (Mar-Jun)</h3>
                </div>
                <p className="text-gray-600 mb-2">
                  <strong>Moderate</strong> - Warm but manageable with evening breezes
                </p>
                <p className="text-sm text-gray-500">Temperature: 25°C - 40°C</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-gold-600" size={24} />
                  <h3 className="text-xl font-bold text-navy-900">Monsoon (Jul-Sep)</h3>
                </div>
                <p className="text-gray-600 mb-2">
                  <strong>Off-season</strong> - Heavy rainfall, challenging terrain
                </p>
                <p className="text-sm text-gray-500">Temperature: 20°C - 35°C</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif">Ready to Experience Haridwar?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your stay with us and explore the spiritual wonders of this sacred city
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-gold-600 hover:bg-gold-700 text-white px-10 py-6 text-lg">
              Book Your Stay Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
